/**
 * LegFaresPanel — every fare surface for one leg, behind one pulldown.
 *
 * WHY. The trip page had three things competing for the same attention: the
 * chosen flight, an always-open "Prices through the day" grid, and a separate
 * "All flight options" pulldown — and then, below all of that, the itinerary.
 * Wade's read: *"I'd like itinerary to be the focal point and PRICES THROUGH
 * THE DAY seem to get in the way."* He is right, and the grid is the worst
 * offender because it is the only one that never closes.
 *
 * The fix is not to hide fare data — it is load-bearing once you are choosing a
 * flight — but to collapse it to ONE affordance that earns its line. Two
 * adjacent pulldowns would have been the lazy read of "make it a pulldown too";
 * one panel is less furniture and its closed state can actually say something:
 *
 *     Fares · from $312 · 47 options            ⌄
 *
 * Closed, that is a useful sentence. Open, it is the grid then the full option
 * list, in the order you would use them. The itinerary below is then the tallest
 * thing on the leg, which is the point.
 */
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { PlanLeg } from "@/lib/types";
import FareMatrixTable from "./FareMatrixTable";
import FlightOptionsList from "./FlightOptionsList";

interface Props {
  tripId: string;
  leg: PlanLeg;
  /** Open on first render. Off by default — that is the whole feature. */
  defaultOpen?: boolean;
}

/** Cheapest fare across the option matrix, or null when there is nothing to price. */
export function cheapestFare(leg: Pick<PlanLeg, "matrix">): number | null {
  let best: number | null = null;
  for (const group of leg.matrix ?? []) {
    for (const f of group.flights ?? []) {
      if (typeof f.price === "number" && f.price > 0 && (best == null || f.price < best)) {
        best = f.price;
      }
    }
  }
  return best;
}

/** Total flights across every date option. */
export function countOptions(leg: Pick<PlanLeg, "matrix">): number {
  return (leg.matrix ?? []).reduce((n, g) => n + (g.flights?.length ?? 0), 0);
}

/**
 * "Fares · from $312 · 47 options" — and gracefully less when we know less.
 * Never renders a bare "Fares · 0 options": if there is nothing to say, the
 * caller does not render the panel at all.
 */
export function faresSummary(leg: Pick<PlanLeg, "matrix">): string {
  const bits = ["Fares"];
  const cheapest = cheapestFare(leg);
  if (cheapest != null) bits.push(`from $${Math.round(cheapest).toLocaleString()}`);
  const n = countOptions(leg);
  if (n > 0) bits.push(`${n} ${n === 1 ? "option" : "options"}`);
  return bits.join(" · ");
}

/** Cells actually present in the time-of-day grid. Mirrors FareMatrixTable. */
export function gridCellCount(matrix: PlanLeg["matrixV2"] | null | undefined): number {
  return (matrix?.gateways ?? []).reduce(
    (n, g) => n + (g.rows ?? []).reduce((m, r) => m + Object.keys(r.cells ?? {}).length, 0),
    0,
  );
}

export default function LegFaresPanel({ tripId, leg, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);

  const hasMatrix = (leg.matrix?.length ?? 0) > 0;
  /*
   * ⚠️ `matrixV2` is a truthy OBJECT even when it holds nothing — a leg with no
   * fares still ships `{timeBuckets:[…], gateways:[]}`. Testing the object
   * rendered an empty pulldown on exactly the legs that have nothing to show.
   * Mirror FareMatrixTable's own emptiness test: count the cells.
   */
  const hasGrid = gridCellCount(leg.matrixV2) > 0;
  // Nothing to show is nothing to offer — an empty pulldown is worse than none.
  if (!hasMatrix && !hasGrid) return null;

  return (
    <div className="mt-3 overflow-hidden rounded-lg border bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 motion-reduce:transition-none"
      >
        <span>{faresSummary(leg)}</span>
        <ChevronDown
          className={
            "h-4 w-4 transition-transform motion-reduce:transition-none " +
            (open ? "rotate-180" : "")
          }
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="border-t px-3 pb-3">
          {/* Price-by-time-of-day first: it is how you decide WHICH option to
              look at, so it belongs above the list of options. */}
          {hasGrid && (
            <FareMatrixTable tripId={tripId} routeId={leg.routeId} matrix={leg.matrixV2} bare />
          )}
          {hasMatrix && (
            <FlightOptionsList
              tripId={tripId}
              routeId={leg.routeId}
              matrix={leg.matrix}
              originCode={leg.origin}
              destinationCode={leg.destination}
              role={leg.role}
              bare
            />
          )}
        </div>
      )}
    </div>
  );
}
